import Alpine from "alpinejs";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

type TitleAnimationOptions = {
  start?: string;
  markers?: boolean;
};

const splitTextToChars = (
  element: HTMLElement,
  className = "title-animation__char"
) => {
  if (element.dataset.titleAnimationSplit === "true") {
    return Array.from(
      element.querySelectorAll<HTMLElement>("[data-title-animation-char]")
    );
  }

  const chars: HTMLElement[] = [];
  const lines: HTMLElement[] = [];
  let currentLine = document.createElement("span");
  let currentLineInner = document.createElement("span");

  const resetCurrentLine = () => {
    currentLine = document.createElement("span");
    currentLine.className = "title-animation__line";
    currentLine.style.display = "block";
    currentLine.style.overflow = "hidden";

    currentLineInner = document.createElement("span");
    currentLineInner.className = "title-animation__line-inner";
    currentLineInner.style.display = "inline-block";
    currentLine.appendChild(currentLineInner);
  };

  const pushCurrentLine = () => {
    if (currentLineInner.childNodes.length === 0) {
      return;
    }

    lines.push(currentLine);
    resetCurrentLine();
  };

  resetCurrentLine();

  const splitNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || "").replace(/[\t\r\n]+/g, "").trim();

      if (!text) {
        return;
      }

      Array.from(text).forEach((char) => {
        const charElement = document.createElement("span");
        charElement.className = className;
        charElement.dataset.titleAnimationChar = "true";
        charElement.style.display = "inline-block";
        charElement.textContent = char === " " ? "\u00A0" : char;
        currentLineInner.appendChild(charElement);
        chars.push(charElement);
      });

      return;
    }

    if (node instanceof HTMLBRElement) {
      pushCurrentLine();
      return;
    }

    Array.from(node.childNodes).forEach(splitNode);
  };

  Array.from(element.childNodes).forEach(splitNode);
  pushCurrentLine();
  element.replaceChildren(...lines);
  element.dataset.titleAnimationSplit = "true";

  return chars;
};

export const initTitleAnimation = () => {
  Alpine.data("titleAnimation", (options: TitleAnimationOptions = {}) => {
    return {
      init() {
        const root = this.$el as HTMLElement;
        const enLabel = this.$refs.enLabel as HTMLElement | undefined;
        const jaLabel = this.$refs.jaLabel as HTMLElement | undefined;

        if (!enLabel || !jaLabel) {
          return;
        }

        const jaLabelChars = splitTextToChars(jaLabel);
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        gsap.set(enLabel, {
          opacity: 0,
          yPercent: 60,
        });
        gsap.set(jaLabelChars, {
          opacity: 0,
          yPercent: 80,
          transformOrigin: "50% 100%",
        });

        if (prefersReducedMotion) {
          gsap.set(enLabel, {
            opacity: 1,
            yPercent: 0,
          });
          gsap.set(jaLabelChars, {
            opacity: 1,
            yPercent: 0,
          });
          return;
        }

        gsap
          .timeline({
            scrollTrigger: {
              trigger: root,
              start: options.start || "top 75%",
              once: true,
              markers: options.markers || false,
            },
          })
          .to(enLabel, {
            opacity: 1,
            yPercent: 0,
            duration: 1.2,
            ease: "power2.out",
          })
          .to(
            jaLabelChars,
            {
              opacity: 1,
              yPercent: 0,
              duration: 0.7,
              ease: "back.out(1.15)",
              stagger: 0.02,
            },
            "0.1"
          );
      },
    };
  });
};
